import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Loader2, Check, PenLine, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RetainerSign() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/retainer/${token}`);
        setMeta(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not load retainer"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineWidth = 2.5; ctx.strokeStyle = "#0f172a";
    let drawing = false;
    const pos = (e) => {
      const rect = c.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };
    const start = (e) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); setHasInk(true); e.preventDefault(); };
    const move = (e) => { if (!drawing) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); window.removeEventListener("touchend", end);
    };
  }, [meta]);

  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false);
  };
  const submit = async () => {
    if (!hasInk) return toast.error("Please draw your signature");
    if (!typedName.trim()) return toast.error("Please type your full legal name");
    if (!agreed) return toast.error("You must agree to the terms");
    setSubmitting(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/api/public/retainer/${token}/sign`, { signature_png: png, typed_name: typedName.trim(), agreed: true });
      toast.success("Signed — thank you!");
      const r = await axios.get(`${API}/api/public/retainer/${token}`);
      setMeta(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to sign"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500"/></div>;
  if (!meta) return <div className="min-h-screen flex items-center justify-center text-slate-500">This link is invalid or expired.</div>;

  const alreadySigned = meta.already_signed;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster richColors position="top-right"/>
      <div className="max-w-4xl mx-auto px-5 py-10 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase text-slate-500">{meta.firm_name}</div>
            <h1 className="heading-font text-3xl font-bold text-slate-900 mt-1">Retainer Agreement</h1>
            <div className="text-sm text-slate-600 mt-1">
              Case <span className="mono">{meta.case.application_number}</span> · {meta.case.app_type_name} · Prepared for {meta.client.full_name}
            </div>
          </div>
          {alreadySigned && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 text-sm flex items-center gap-2">
              <Check className="w-4 h-4"/>Already signed{meta.signed_at ? ` on ${meta.signed_at.slice(0,10)}` : ""}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase text-slate-500">Review the agreement</div>
          <iframe
            title="Retainer PDF"
            src={`${API}/api/public/retainer/${token}/pdf#toolbar=1&view=FitH`}
            className="w-full"
            style={{ height: "70vh", border: 0 }}
          />
        </div>

        {!alreadySigned && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2"><PenLine className="w-4 h-4 text-orange-500"/>Draw your signature</div>
                <div className="text-xs text-slate-500 mt-0.5">Use your mouse or finger inside the box.</div>
              </div>
              <Button variant="outline" size="sm" onClick={clearCanvas} data-testid="retainer-clear"><RotateCcw className="w-3 h-3 mr-1"/>Clear</Button>
            </div>
            <canvas
              ref={canvasRef}
              width={760}
              height={200}
              className="w-full rounded-lg border-2 border-dashed border-slate-300 bg-white cursor-crosshair touch-none"
              data-testid="retainer-canvas"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-600">Type your full legal name</Label>
                <Input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="e.g. John Smith" data-testid="retainer-typed-name"/>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700 pt-6">
                <Checkbox checked={agreed} onCheckedChange={v => setAgreed(!!v)} data-testid="retainer-agree"/>
                <span>I have read and agree to the terms of this Retainer Agreement.</span>
              </label>
            </div>
            <div className="flex items-center justify-end">
              <Button
                onClick={submit}
                disabled={submitting || !hasInk || !agreed || !typedName.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="retainer-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <PenLine className="w-4 h-4 mr-1"/>}Submit Signed Retainer
              </Button>
            </div>
          </div>
        )}

        <div className="text-xs text-slate-400 text-center">
          Secured by {meta.firm_name}. If you did not request this, you can safely ignore this page.
        </div>
      </div>
    </div>
  );
}
